import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { KioskInfo } from './apiKiosk';
import type { QmsToken } from './types';

function formatTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * The issued-token ticket: the centre's branding wrapped around the queue
 * number. It is the on-screen confirmation on the kiosk and, because it is
 * a self-contained white card, the exact node snapshotted to a PNG when a
 * visitor taps Save. A centre with no logo falls back to its name. The
 * logo is streamed from our own API (`/qms/public/kiosk/logo`), so it is
 * same-origin with the page and inlines cleanly into the saved PNG — no
 * cross-origin storage CORS policy required.
 */
export const KioskTokenTicket = forwardRef<
  HTMLDivElement,
  {
    info: KioskInfo;
    token: QmsToken;
    serviceName: string;
    serviceNameAr?: string | null;
    locale: string;
  }
>(function KioskTokenTicket(
  { info, token, serviceName, serviceNameAr, locale },
  ref,
) {
  const { t } = useTranslation('qms');
  return (
    <div
      ref={ref}
      data-testid="kiosk-token-ticket"
      className="flex w-80 flex-col items-center gap-1 bg-white px-8 py-9 text-center text-neutral-900"
    >
      {info.logo_url ? (
        <img
          src={info.logo_url}
          alt={info.tenant_name}
          className="mb-2 h-16 w-auto max-w-[12rem] object-contain"
        />
      ) : (
        <p className="mb-2 text-xl font-bold">{info.tenant_name}</p>
      )}
      {info.header_text ? (
        <p className="whitespace-pre-wrap text-xs text-neutral-500">
          {info.header_text}
        </p>
      ) : null}
      <p className="mt-3 text-xs uppercase tracking-[0.2em] text-neutral-400">
        {t('kiosk.your_token')}
      </p>
      <p className="my-1 font-mono text-7xl font-bold tracking-tight text-neutral-900">
        {token.display_code}
      </p>
      <p className="text-base font-medium">{serviceName}</p>
      {serviceNameAr ? (
        <p dir="rtl" lang="ar" className="text-base font-medium">
          {serviceNameAr}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-neutral-400">
        {t('print.issued_at')}: {formatTime(token.issued_at, locale)}
      </p>
      {info.footer_text ? (
        <p className="mt-3 whitespace-pre-wrap text-xs text-neutral-500">
          {info.footer_text}
        </p>
      ) : null}
    </div>
  );
});
